import { cn } from "@u/cn";
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover";
import { Command } from "cmdk";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Extra terms the search should match beyond `label`. */
  keywords?: string[];
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  /** Prepend a clear/"none" entry that maps to an empty string. */
  noneLabel?: string;
  /** Sort options alphabetically by label (locale-aware). Default true. */
  sort?: boolean;
  disabled?: boolean;
  id?: string;
  /** Extra classes for the trigger button. */
  className?: string;
  /** Extra classes for the popover content. */
  contentClassName?: string;
}

/**
 * Branded searchable select. A compact trigger that opens a filterable list
 * in a popover — for dropdowns with many or data-driven options (contacts,
 * tours, venues, users…). Short fixed enums should stay on plain `<Select>`.
 * Popover portals above modals (z-[130]), so it works inside dialogs/sheets.
 */
export function SearchSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  noneLabel,
  sort = true,
  disabled = false,
  id,
  className,
  contentClassName,
}: Props) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const items = useMemo(() => {
    if (!sort) return options;
    return [...options].sort((a, b) =>
      a.label.localeCompare(b.label, i18n.language),
    );
  }, [options, sort, i18n.language]);

  const current = options.find((o) => o.value === value);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        disabled={disabled}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-border bg-transparent px-3 text-sm transition-colors hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          !current && "text-muted-foreground",
          className,
        )}
      >
        <span className="truncate">
          {current ? current.label : (placeholder ?? "—")}
        </span>
        <ChevronsUpDown size={14} className="shrink-0 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          "w-(--radix-popover-trigger-width) gap-0 p-0",
          contentClassName,
        )}
      >
        <Command className="flex flex-col" loop>
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search size={14} className="shrink-0 text-muted-foreground" />
            <Command.Input
              placeholder={searchPlaceholder ?? t("search", "Buscar…")}
              className="h-9 w-full bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
            />
          </div>
          <Command.List className="max-h-64 overflow-y-auto p-1">
            <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
              {emptyLabel ?? t("no_results", "Sin resultados")}
            </Command.Empty>
            {noneLabel && (
              <Command.Item
                value="__none__"
                keywords={[noneLabel]}
                onSelect={() => pick("")}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground data-[selected=true]:bg-muted"
              >
                <X
                  size={14}
                  className={cn("shrink-0", !value ? "opacity-100" : "opacity-0")}
                />
                {noneLabel}
              </Command.Item>
            )}
            {items.map((o) => (
              <Command.Item
                key={o.value}
                value={o.value}
                keywords={[o.label, ...(o.keywords ?? [])]}
                onSelect={() => pick(o.value)}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm data-[selected=true]:bg-muted"
              >
                <Check
                  size={14}
                  className={cn(
                    "shrink-0",
                    o.value === value ? "opacity-100" : "opacity-0",
                  )}
                />
                <span className="truncate">{o.label}</span>
              </Command.Item>
            ))}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
